import React, { useState } from 'react';
import { createPortal } from 'react-dom';
import { motion } from 'framer-motion';

// Animation variants following design reference
const cardVariants = {
  hidden: { opacity: 0, y: 20 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.6, ease: "easeOut" }
  }
};

const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.1
    }
  }
};

// Compact token badge components
const XRPToken = () => (
  <div className="w-6 h-6 rounded-full overflow-hidden border border-blue-500/20 shadow-md">
    <img src="/images/XRP.png" alt="XRP" className="w-full h-full object-cover" />
  </div>
);

const RLUSDToken = () => (
  <div className="w-6 h-6 rounded-full overflow-hidden border border-blue-500/20 shadow-md">
    <img src="/images/RLUSD.png" alt="RLUSD" className="w-full h-full object-cover" />
  </div>
);

const Wallet: React.FC = () => {
  const [activeTab, setActiveTab] = useState('balance');
  const [showWithdrawModal, setShowWithdrawModal] = useState(false);
  const [showDepositModal, setShowDepositModal] = useState(false);
  const [showTopUpModal, setShowTopUpModal] = useState(false);
  const [showManageCardModal, setShowManageCardModal] = useState(false);
  const [withdrawAmount, setWithdrawAmount] = useState('');
  const [selectedCurrency, setSelectedCurrency] = useState('USD');
  
  // Mock KYC status: 0 = not started, 1 = identity verified, 2 = address verified, 3 = completed
  const [kycStatus, setKycStatus] = useState<number>(1);
  
  // Deposit Modal Component
  const DepositModal = () => {
    const [amount, setAmount] = useState<string>('');
    const [selectedAsset, setSelectedAsset] = useState<'XRP' | 'RLUSD' | 'ETH'>('RLUSD');
    const [isProcessing, setIsProcessing] = useState(false);
    const [errors, setErrors] = useState<{amount?: string}>({});
    
    // Prevent body scroll when modal is open
    React.useEffect(() => {
      document.body.style.overflow = 'hidden';
      return () => {
        document.body.style.overflow = 'unset';
      };
    }, []);
    
    const assetInfo = {
      XRP: { name: 'XRP', icon: '/images/XRP.png', balance: '1,250.00' },
      RLUSD: { name: 'Ripple USD', icon: '/images/RLUSD.png', balance: '5,000.00' },
      ETH: { name: 'Ethereum', icon: '/images/ethereum.svg', balance: '2.5678' }
    };

    const validateAmount = (value: string) => {
      const numValue = parseFloat(value);
      if (!value || value.trim() === '') {
        return 'Amount is required';
      }
      if (isNaN(numValue) || numValue <= 0) {
        return 'Please enter a valid amount';
      }
      if (numValue < 0.01) {
        return 'Minimum deposit amount is 0.01';
      }
      return '';
    };

    const handleAmountChange = (value: string) => {
      setAmount(value);
      const error = validateAmount(value);
      setErrors(prev => ({ ...prev, amount: error }));
    };

    const handleSetMax = () => {
      const maxAmount = parseFloat(assetInfo[selectedAsset].balance.replace(',', ''));
      setAmount(maxAmount.toString());
      setErrors(prev => ({ ...prev, amount: '' }));
    };

    const handleDeposit = async () => {
      if (!amount || parseFloat(amount) <= 0) return;
      
      setIsProcessing(true);
      // Simulate API call
      await new Promise(resolve => setTimeout(resolve, 2000));
      setIsProcessing(false);
      setShowDepositModal(false);
      setAmount('');
    };

    const modalContent = (
      <div className="fixed top-0 left-0 right-0 bottom-0 bg-slate-900/80 backdrop-blur-xl flex items-center justify-center z-[9999] p-4">
        <motion.div 
          className="rounded-xl p-5 bg-gradient-to-br from-slate-800/50 via-blue-900/10 to-slate-900/50 backdrop-blur-xl border border-blue-500/20 w-full max-w-md"
          initial={{ opacity: 0, scale: 0.9, y: 20 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.9, y: 20 }}
          transition={{ duration: 0.3 }}
        >
          {/* Animated background pattern */}
          <div className="absolute inset-0 bg-gradient-to-r from-blue-600/5 via-transparent to-blue-600/5 animate-pulse rounded-xl"></div>
          
          <div className="relative z-10">
            {/* Header */}
            <div className="flex justify-between items-center mb-5">
              <h2 className="text-xl font-semibold text-white">
                Deposit to <span className="bg-gradient-to-r from-blue-400 to-blue-600 bg-clip-text text-transparent">Wallet</span>
              </h2>
              <button 
                onClick={() => setShowDepositModal(false)} 
                className="p-1.5 rounded-lg hover:bg-slate-800/50 transition-colors text-slate-400 hover:text-white"
              >
                <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" className="w-4 h-4">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            
            <div className="space-y-4">
              {/* Asset Selection */}
              <div>
                <label className="block text-xs font-medium text-slate-400 mb-2 uppercase tracking-wider">Select Asset</label>
                <div className="grid grid-cols-3 gap-2">
                  {(Object.keys(assetInfo) as Array<keyof typeof assetInfo>).map((asset) => (
                    <button 
                      key={asset}
                      onClick={() => setSelectedAsset(asset)}
                      className={`p-3 rounded-xl border transition-all duration-300 ${
                        selectedAsset === asset
                          ? 'bg-gradient-to-br from-blue-500/20 to-blue-600/20 border-blue-500/40 text-white'
                          : 'bg-slate-800/50 border-slate-700/50 text-slate-300 hover:border-blue-400/40'
                      }`}
                    >
                      <div className="flex flex-col items-center gap-2">
                        <img src={assetInfo[asset].icon} alt={asset} className="w-6 h-6" />
                        <span className="text-xs font-medium">{asset}</span>
                      </div>
                    </button>
                  ))}
                </div>
              </div>

              {/* Amount Input */}
              <div>
                <label className="block text-xs font-medium text-slate-400 mb-2 uppercase tracking-wider">Amount</label>
                <div className="relative">
                  <input 
                    type="text" 
                    placeholder="0.00"
                    value={amount}
                    onChange={(e) => handleAmountChange(e.target.value)}
                    className="w-full bg-slate-800/50 border border-slate-700/50 rounded-xl p-3 text-lg font-semibold text-white placeholder-slate-400 focus:border-blue-500/40 focus:outline-none transition-all duration-500" 
                  />
                  <div className="absolute right-2.5 top-1/2 -translate-y-1/2 bg-slate-800/50 px-2.5 py-1.5 rounded-lg flex items-center gap-1.5 border border-slate-700/50">
                    <img src={assetInfo[selectedAsset].icon} alt={selectedAsset} className="w-4 h-4 rounded-full object-cover" />
                    <span className="font-medium text-xs text-white">{selectedAsset}</span>
                  </div>
                </div>
                <div className="flex justify-between text-xs mt-1.5">
                  <span className="text-slate-400">Available: {assetInfo[selectedAsset].balance} {selectedAsset}</span>
                  <button 
                    onClick={handleSetMax}
                    className="text-blue-400 hover:text-blue-300 transition-colors font-medium"
                  >
                    Max
                  </button>
                </div>
                {errors.amount && (
                  <p className="text-red-400 text-xs mt-1">{errors.amount}</p>
                )}
              </div>
              
              {/* Deposit Info */}
              <div className="bg-slate-800/50 backdrop-blur-xl rounded-xl p-4 border border-slate-700/50 space-y-2 text-xs">
                <div className="flex justify-between">
                  <span className="text-slate-400">Network</span>
                  <span className="font-medium text-white">XRPL</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-400">Processing Time</span>
                  <span className="font-medium text-white">~3-5 minutes</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-400">Network Fee</span>
                  <span className="font-medium text-white">0.000012 XRP</span>
                </div>
              </div>
              
              <motion.button 
                onClick={handleDeposit}
                disabled={!amount || !!errors.amount || isProcessing}
                className="w-full bg-gradient-to-r from-blue-500 via-blue-400 to-blue-600 text-white font-medium py-3 px-6 rounded-xl disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-300"
                whileHover={{ 
                  scale: 1.02,
                  boxShadow: "0 0 30px rgba(59, 130, 246, 0.3)"
                }}
                whileTap={{ scale: 0.98 }}
              >
                <span className="relative z-10 text-sm flex items-center justify-center gap-2">
                  {isProcessing ? (
                    <>
                      <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                      Processing...
                    </>
                  ) : (
                    `Deposit ${amount ? `${amount} ${selectedAsset}` : 'Funds'}`
                  )}
                </span>
              </motion.button>
            </div>
          </div>
        </motion.div>
      </div>
    );

    return createPortal(modalContent, document.body);
  };
  
  // Withdraw Modal Component - Clean & Minimalistic
  const WithdrawModal = () => {
    const [amount, setAmount] = useState<string>('');
    const [withdrawCurrency, setWithdrawCurrency] = useState<'USD' | 'EUR' | 'GBP'>('USD');
    const [isProcessing, setIsProcessing] = useState(false);
    const [errors, setErrors] = useState<{amount?: string}>({});
    const [showTransactionDetails, setShowTransactionDetails] = useState(false);
    
    // Prevent body scroll when modal is open
    React.useEffect(() => {
      console.log('Wallet modal is opening');
      document.body.style.overflow = 'hidden';
      return () => {
        console.log('Wallet modal is closing');
        document.body.style.overflow = 'unset';
      };
    }, []);
    
    const currencyRates = {
      USD: 1,
      EUR: 0.92,
      GBP: 0.79
    };

    const validateAmount = (value: string) => {
      const numValue = parseFloat(value);
      if (!value || value.trim() === '') {
        return 'Amount is required';
      }
      if (isNaN(numValue) || numValue <= 0) {
        return 'Please enter a valid amount';
      }
      if (numValue > 5000) {
        return 'Insufficient balance';
      }
      if (numValue < 1) {
        return 'Minimum withdrawal amount is 1 RLUSD';
      }
      return '';
    };

    const handleAmountChange = (value: string) => {
      setAmount(value);
      const error = validateAmount(value);
      setErrors(prev => ({ ...prev, amount: error }));
    };

    const handleSetMax = () => {
      handleAmountChange('5000');
    };

    const handleWithdraw = async () => {
      const amountError = validateAmount(amount);
      if (amountError) {
        setErrors({ amount: amountError });
        return;
      }

      setIsProcessing(true);
      // Simulate processing time
      setTimeout(() => {
        setIsProcessing(false);
        setShowWithdrawModal(false);
        setAmount('');
        setErrors({});
        // Show success notification here
      }, 2000);
    };

    const getReceivedAmount = () => {
      if (!amount) return '0.00';
      const rlusdAmount = parseFloat(amount);
      const fee = rlusdAmount * 0.005; // 0.5% fee
      const afterFee = rlusdAmount - fee;
      const converted = afterFee * currencyRates[withdrawCurrency];
      return converted.toFixed(2);
    };
    
    const modalContent = (
      <div className="fixed top-0 left-0 right-0 bottom-0 bg-slate-900/80 backdrop-blur-xl flex items-center justify-center z-[9999] p-4">
        <motion.div 
          className="rounded-xl p-5 bg-gradient-to-br from-slate-800/50 via-blue-900/10 to-slate-900/50 backdrop-blur-xl border border-blue-500/20 w-full max-w-md"
          initial={{ opacity: 0, scale: 0.9, y: 20 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.9, y: 20 }}
          transition={{ duration: 0.3 }}
        >
          {/* Animated background pattern */}
          <div className="absolute inset-0 bg-gradient-to-r from-blue-600/5 via-transparent to-blue-600/5 animate-pulse rounded-xl"></div>
          
          <div className="relative z-10">
            {/* Header */}
            <div className="flex justify-between items-center mb-5">
              <h2 className="text-xl font-semibold text-white">
                Withdraw to <span className="bg-gradient-to-r from-blue-400 to-blue-600 bg-clip-text text-transparent">Card</span>
              </h2>
              <button 
                onClick={() => setShowWithdrawModal(false)} 
                className="p-1.5 rounded-lg hover:bg-slate-800/50 transition-colors text-slate-400 hover:text-white"
              >
                <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" className="w-4 h-4">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
              
            <div className="space-y-4">
              {/* Amount Input */}
              <div>
                <label className="block text-xs font-medium text-slate-400 mb-2 uppercase tracking-wider">Amount</label>
                <div className="relative">
                  <input 
                    type="text" 
                    placeholder="0.00"
                    value={amount}
                    onChange={(e) => handleAmountChange(e.target.value)}
                    className="w-full bg-slate-800/50 border border-slate-700/50 rounded-xl p-3 text-lg font-semibold text-white placeholder-slate-400 focus:border-blue-500/40 focus:outline-none transition-all duration-500" 
                  />
                  <div className="absolute right-2.5 top-1/2 -translate-y-1/2 bg-slate-800/50 px-2.5 py-1.5 rounded-lg flex items-center gap-1.5 border border-slate-700/50">
                    <img src="/images/RLUSD.png" alt="RLUSD" className="w-4 h-4 rounded-full object-cover" />
                    <span className="font-medium text-xs text-white">RLUSD</span>
                  </div>
                </div>
                <div className="flex justify-between text-xs mt-1.5">
                  <span className="text-slate-400">Available: 3,247.89 RLUSD</span>
                  <button 
                    onClick={handleSetMax}
                    className="text-blue-400 hover:text-blue-300 transition-colors font-medium"
                  >
                    Max
                  </button>
                </div>
                {errors.amount && (
                  <p className="text-red-400 text-xs mt-1">{errors.amount}</p>
                )}
              </div>

              {/* Currency Selection */}
              <div>
                <label className="block text-xs font-medium text-slate-400 mb-2 uppercase tracking-wider">Withdraw as</label>
                <div className="grid grid-cols-3 gap-2">
                  {(['USD', 'EUR', 'GBP'] as const).map((currency) => (
                    <button
                      key={currency}
                      onClick={() => setWithdrawCurrency(currency)}
                      className={`py-2.5 px-3 rounded-lg text-xs font-medium transition-all duration-300 ${
                        withdrawCurrency === currency
                          ? 'bg-gradient-to-r from-blue-500 to-blue-600 text-white shadow-lg shadow-blue-500/30'
                          : 'bg-slate-800/50 border border-slate-700/50 text-slate-300 hover:border-blue-400/40'
                      }`}
                    >
                      {currency}
                    </button>
                  ))}
                </div>
              </div>

              {/* Transaction Summary */}
              <div className="bg-slate-800/50 backdrop-blur-xl rounded-xl p-4 border border-slate-700/50 space-y-2 text-xs">
                <div className="flex justify-between">
                  <span className="text-slate-400">Exchange Rate</span>
                  <span className="font-medium text-white">1 RLUSD = {currencyRates[withdrawCurrency].toFixed(2)} {withdrawCurrency}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-400">Processing Fee</span>
                  <span className="font-medium text-white">0.5%</span>
                </div>
                <div className="flex justify-between border-t border-slate-700/50 pt-2">
                  <span className="text-slate-400 font-medium">You'll Receive</span>
                  <span className="font-semibold text-white text-sm">{getReceivedAmount()} {withdrawCurrency}</span>
                </div>
              </div>
              
              <motion.button 
                onClick={handleWithdraw}
                disabled={!amount || !!errors.amount || isProcessing}
                className="w-full bg-gradient-to-r from-blue-500 via-blue-400 to-blue-600 text-white font-medium py-3 px-6 rounded-xl disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-300"
                whileHover={{ 
                  scale: 1.02,
                  boxShadow: "0 0 30px rgba(59, 130, 246, 0.3)"
                }}
                whileTap={{ scale: 0.98 }}
              >
                <span className="relative z-10 text-sm flex items-center justify-center gap-2">
                  {isProcessing ? (
                    <>
                      <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                      Processing...
                    </>
                  ) : (
                    'Confirm Withdrawal'
                  )}
                </span>
              </motion.button>
            </div>
          </div>
        </motion.div>
      </div>
    );

        return createPortal(modalContent, document.body);
  };

  // Top Up Card Modal Component
  const TopUpCardModal = () => {
    const [amount, setAmount] = useState<string>('');
    const [selectedSource, setSelectedSource] = useState<'wallet' | 'bank'>('wallet');
    const [isProcessing, setIsProcessing] = useState(false);
    const [errors, setErrors] = useState<{amount?: string}>({});
    
    // Prevent body scroll when modal is open
    React.useEffect(() => {
      document.body.style.overflow = 'hidden';
      return () => {
        document.body.style.overflow = 'unset';
      };
    }, []);

    const validateAmount = (value: string) => {
      const numValue = parseFloat(value);
      if (!value || value.trim() === '') {
        return 'Amount is required';
      }
      if (isNaN(numValue) || numValue <= 0) {
        return 'Please enter a valid amount';
      }
      if (numValue < 10) {
        return 'Minimum top-up amount is $10';
      }
      if (numValue > 5000) {
        return 'Maximum top-up amount is $5,000';
      }
      return '';
    };

    const handleAmountChange = (value: string) => {
      setAmount(value);
      const error = validateAmount(value);
      setErrors(prev => ({ ...prev, amount: error }));
    };

    const handleTopUp = async () => {
      if (!amount || parseFloat(amount) <= 0) return;
      
      setIsProcessing(true);
      // Simulate API call
      await new Promise(resolve => setTimeout(resolve, 2000));
      setIsProcessing(false);
      setShowTopUpModal(false);
      setAmount('');
    };

    const modalContent = (
      <div className="fixed top-0 left-0 right-0 bottom-0 bg-slate-900/80 backdrop-blur-xl flex items-center justify-center z-[9999] p-4">
        <motion.div 
          className="rounded-xl p-6 bg-gradient-to-br from-slate-800/50 via-blue-900/10 to-slate-900/50 backdrop-blur-xl border border-blue-500/20 w-full max-w-md"
          initial={{ opacity: 0, scale: 0.9, y: 20 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.9, y: 20 }}
          transition={{ duration: 0.3 }}
        >
          {/* Animated background pattern */}
          <div className="absolute inset-0 bg-gradient-to-r from-blue-600/5 via-transparent to-blue-600/5 animate-pulse rounded-xl"></div>
          
          <div className="relative z-10">
            {/* Header */}
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-xl font-semibold text-white">
                Top Up <span className="bg-gradient-to-r from-blue-400 to-blue-600 bg-clip-text text-transparent">Card</span>
              </h2>
              <button 
                onClick={() => setShowTopUpModal(false)} 
                className="p-1.5 rounded-lg hover:bg-slate-800/50 transition-colors text-slate-400 hover:text-white"
              >
                <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" className="w-4 h-4">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            
            <div className="space-y-4">
              {/* Current Card Balance */}
              <div className="bg-slate-800/50 backdrop-blur-xl rounded-xl p-4 border border-slate-700/50">
                <div className="text-center">
                  <div className="text-xs font-medium text-slate-400 mb-2 uppercase tracking-wider">Current Card Balance</div>
                  <div className="text-2xl font-semibold bg-gradient-to-r from-blue-400 to-blue-600 bg-clip-text text-transparent">$750.00</div>
                </div>
              </div>

              {/* Top-up Source */}
              <div>
                <label className="block text-xs font-medium text-slate-400 mb-2 uppercase tracking-wider">Top-up Source</label>
                <div className="grid grid-cols-2 gap-2">
                  <button
                    onClick={() => setSelectedSource('wallet')}
                    className={`p-3 rounded-xl border transition-all duration-300 ${
                      selectedSource === 'wallet'
                        ? 'bg-gradient-to-br from-blue-500/20 to-blue-600/20 border-blue-500/40 text-white'
                        : 'bg-slate-800/50 border-slate-700/50 text-slate-300 hover:border-blue-400/40'
                    }`}
                  >
                    <div className="flex flex-col items-center gap-2">
                      <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z" />
                      </svg>
                      <span className="text-xs font-medium">Wallet</span>
                      <span className="text-xs text-slate-400">$8,247.89</span>
                    </div>
                  </button>
                  <button
                    onClick={() => setSelectedSource('bank')}
                    className={`p-3 rounded-xl border transition-all duration-300 ${
                      selectedSource === 'bank'
                        ? 'bg-gradient-to-br from-blue-500/20 to-blue-600/20 border-blue-500/40 text-white'
                        : 'bg-slate-800/50 border-slate-700/50 text-slate-300 hover:border-blue-400/40'
                    }`}
                  >
                    <div className="flex flex-col items-center gap-2">
                      <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                      </svg>
                      <span className="text-xs font-medium">Bank</span>
                      <span className="text-xs text-slate-400">****1234</span>
                    </div>
                  </button>
                </div>
              </div>

              {/* Amount Input */}
              <div>
                <label className="block text-xs font-medium text-slate-400 mb-2 uppercase tracking-wider">Amount (USD)</label>
                <div className="relative">
                  <div className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 font-medium">$</div>
                  <input 
                    type="text" 
                    placeholder="0.00"
                    value={amount}
                    onChange={(e) => handleAmountChange(e.target.value)}
                    className="w-full bg-slate-800/50 border border-slate-700/50 rounded-xl p-3 pl-8 text-lg font-semibold text-white placeholder-slate-400 focus:border-blue-500/40 focus:outline-none transition-all duration-500" 
                  />
                </div>
                <div className="flex justify-between text-xs mt-1.5">
                  <span className="text-slate-400">Min: $10 • Max: $5,000</span>
                  <div className="flex gap-2">
                    {[100, 250, 500].map((preset) => (
                      <button
                        key={preset}
                        onClick={() => handleAmountChange(preset.toString())}
                        className="text-blue-400 hover:text-blue-300 transition-colors font-medium"
                      >
                        ${preset}
                      </button>
                    ))}
                  </div>
                </div>
                {errors.amount && (
                  <p className="text-red-400 text-xs mt-1">{errors.amount}</p>
                )}
              </div>

              {/* Transaction Details */}
              <div className="bg-slate-800/50 backdrop-blur-xl rounded-xl p-4 border border-slate-700/50 space-y-2 text-xs">
                <div className="flex justify-between">
                  <span className="text-slate-400">Processing Fee</span>
                  <span className="font-medium text-white">$0.00</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-400">Processing Time</span>
                  <span className="font-medium text-white">Instant</span>
                </div>
                <div className="flex justify-between border-t border-slate-700/50 pt-2">
                  <span className="text-slate-400 font-medium">Total to Add</span>
                  <span className="font-semibold text-white text-sm">${amount || '0.00'}</span>
                </div>
              </div>
              
              <motion.button 
                onClick={handleTopUp}
                disabled={!amount || !!errors.amount || isProcessing}
                className="w-full bg-gradient-to-r from-blue-500 via-blue-400 to-blue-600 text-white font-medium py-3 px-6 rounded-xl disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-300"
                whileHover={{ 
                  scale: 1.02,
                  boxShadow: "0 0 30px rgba(59, 130, 246, 0.3)"
                }}
                whileTap={{ scale: 0.98 }}
              >
                <span className="relative z-10 text-sm flex items-center justify-center gap-2">
                  {isProcessing ? (
                    <>
                      <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                      Processing...
                    </>
                  ) : (
                    `Add $${amount || '0.00'} to Card`
                  )}
                </span>
              </motion.button>
            </div>
          </div>
        </motion.div>
      </div>
    );

    return createPortal(modalContent, document.body);
  };

  // Manage Card Modal Component
  const ManageCardModal = () => {
    const [cardStatus, setCardStatus] = useState<'active' | 'frozen'>('active');
    const [showSettings, setShowSettings] = useState(false);
    
    // Prevent body scroll when modal is open
    React.useEffect(() => {
      document.body.style.overflow = 'hidden';
      return () => {
        document.body.style.overflow = 'unset';
      };
    }, []);

    const toggleCardStatus = () => {
      setCardStatus(prev => prev === 'active' ? 'frozen' : 'active');
    };

    const modalContent = (
      <div className="fixed top-0 left-0 right-0 bottom-0 bg-slate-900/80 backdrop-blur-xl flex items-center justify-center z-[9999] p-4">
        <motion.div 
          className="rounded-xl p-6 bg-gradient-to-br from-slate-800/50 via-blue-900/10 to-slate-900/50 backdrop-blur-xl border border-blue-500/20 w-full max-w-md"
          initial={{ opacity: 0, scale: 0.9, y: 20 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.9, y: 20 }}
          transition={{ duration: 0.3 }}
        >
          {/* Animated background pattern */}
          <div className="absolute inset-0 bg-gradient-to-r from-blue-600/5 via-transparent to-blue-600/5 animate-pulse rounded-xl"></div>
          
          <div className="relative z-10">
            {/* Header */}
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-xl font-semibold text-white">
                Manage <span className="bg-gradient-to-r from-blue-400 to-blue-600 bg-clip-text text-transparent">Card</span>
              </h2>
              <button 
                onClick={() => setShowManageCardModal(false)} 
                className="p-1.5 rounded-lg hover:bg-slate-800/50 transition-colors text-slate-400 hover:text-white"
              >
                <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" className="w-4 h-4">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            
            <div className="space-y-4">
              {/* Card Status */}
              <div className="bg-slate-800/50 backdrop-blur-xl rounded-xl p-4 border border-slate-700/50">
                <div className="flex items-center justify-between">
                  <div>
                    <div className="text-sm font-medium text-white mb-1">Card Status</div>
                    <div className="flex items-center gap-2">
                      <div className={`w-2 h-2 rounded-full ${cardStatus === 'active' ? 'bg-green-400' : 'bg-red-400'} animate-pulse`}></div>
                      <span className={`text-sm font-medium ${cardStatus === 'active' ? 'text-green-400' : 'text-red-400'}`}>
                        {cardStatus === 'active' ? 'Active' : 'Frozen'}
                      </span>
                    </div>
                  </div>
                  <motion.button
                    onClick={toggleCardStatus}
                    className={`px-4 py-2 rounded-lg text-xs font-medium transition-all duration-300 ${
                      cardStatus === 'active' 
                        ? 'bg-red-500/20 text-red-400 hover:bg-red-500/30'
                        : 'bg-green-500/20 text-green-400 hover:bg-green-500/30'
                    }`}
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                  >
                    {cardStatus === 'active' ? 'Freeze Card' : 'Unfreeze Card'}
                  </motion.button>
                </div>
              </div>

              {/* Quick Actions */}
              <div>
                <label className="block text-xs font-medium text-slate-400 mb-3 uppercase tracking-wider">Quick Actions</label>
                <div className="grid grid-cols-2 gap-3">
                  <motion.button
                    className="p-4 bg-slate-800/50 border border-slate-700/50 rounded-xl hover:border-blue-400/40 transition-all duration-300"
                    whileHover={{ scale: 1.02 }}
                  >
                    <div className="flex flex-col items-center gap-2">
                      <svg className="w-5 h-5 text-blue-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                      </svg>
                      <span className="text-xs font-medium text-white">View PIN</span>
                    </div>
                  </motion.button>
                  <motion.button
                    className="p-4 bg-slate-800/50 border border-slate-700/50 rounded-xl hover:border-blue-400/40 transition-all duration-300"
                    whileHover={{ scale: 1.02 }}
                  >
                    <div className="flex flex-col items-center gap-2">
                      <svg className="w-5 h-5 text-blue-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                      </svg>
                      <span className="text-xs font-medium text-white">Reset PIN</span>
                    </div>
                  </motion.button>
                  <motion.button
                    className="p-4 bg-slate-800/50 border border-slate-700/50 rounded-xl hover:border-blue-400/40 transition-all duration-300"
                    whileHover={{ scale: 1.02 }}
                  >
                    <div className="flex flex-col items-center gap-2">
                      <svg className="w-5 h-5 text-blue-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                      <span className="text-xs font-medium text-white">Set Limits</span>
                    </div>
                  </motion.button>
                  <motion.button
                    className="p-4 bg-slate-800/50 border border-slate-700/50 rounded-xl hover:border-blue-400/40 transition-all duration-300"
                    whileHover={{ scale: 1.02 }}
                  >
                    <div className="flex flex-col items-center gap-2">
                      <svg className="w-5 h-5 text-blue-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                      </svg>
                      <span className="text-xs font-medium text-white">Cancel Card</span>
                    </div>
                  </motion.button>
                </div>
              </div>

              {/* Card Settings */}
              <div className="bg-slate-800/50 backdrop-blur-xl rounded-xl p-4 border border-slate-700/50">
                <div className="flex items-center justify-between mb-3">
                  <span className="text-sm font-medium text-white">Settings</span>
                  <button
                    onClick={() => setShowSettings(!showSettings)}
                    className="text-blue-400 hover:text-blue-300 transition-colors"
                  >
                    <svg className={`w-4 h-4 transition-transform ${showSettings ? 'rotate-180' : ''}`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                    </svg>
                  </button>
                </div>
                {showSettings && (
                  <div className="space-y-3 text-xs">
                    <div className="flex justify-between items-center">
                      <span className="text-slate-400">Online Transactions</span>
                      <div className="w-8 h-4 bg-blue-500 rounded-full relative">
                        <div className="w-3 h-3 bg-white rounded-full absolute top-0.5 right-0.5"></div>
                      </div>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-slate-400">ATM Withdrawals</span>
                      <div className="w-8 h-4 bg-blue-500 rounded-full relative">
                        <div className="w-3 h-3 bg-white rounded-full absolute top-0.5 right-0.5"></div>
                      </div>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-slate-400">Contactless Payments</span>
                      <div className="w-8 h-4 bg-slate-600 rounded-full relative">
                        <div className="w-3 h-3 bg-white rounded-full absolute top-0.5 left-0.5"></div>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </motion.div>
      </div>
    );

    return createPortal(modalContent, document.body);
  };
  
  return (
    <motion.div 
      className="space-y-6"
      variants={containerVariants}
      initial="hidden"
      animate="visible"
    >
      {/* Enhanced Header Section with Background Pattern */}
      <motion.div 
        className="relative overflow-hidden rounded-xl p-6 bg-gradient-to-br from-slate-800/50 via-blue-900/10 to-slate-900/50 backdrop-blur-xl border border-blue-500/20"
      >
        {/* Animated background pattern */}
        <div className="absolute inset-0 bg-gradient-to-r from-blue-600/5 via-transparent to-blue-600/5 animate-pulse"></div>
        
        <div className="relative z-10">
          <h1 className="text-2xl font-semibold mb-2">
            <span className="text-white">RippleOne </span>
            <span className="bg-gradient-to-r from-blue-400 to-blue-600 bg-clip-text text-transparent">Wallet</span>
          </h1>
          <p className="text-slate-300 text-sm mb-6 max-w-2xl">
            Manage your digital assets with advanced DeFi features and real-world spending
          </p>
          
          {/* Stats Grid */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <motion.div 
              className="p-4 bg-slate-900/50 backdrop-blur-xl rounded-xl border border-slate-700/50 hover:border-blue-400/40 transition-all duration-300"
              whileHover={{ scale: 1.02 }}
            >
              <div className="text-2xl font-semibold text-white mb-1">$8,247.89</div>
              <div className="text-xs font-medium text-slate-400 tracking-wider uppercase">Total Balance</div>
            </motion.div>
            <motion.div 
              className="p-4 bg-slate-900/50 backdrop-blur-xl rounded-xl border border-slate-700/50 hover:border-blue-400/40 transition-all duration-300"
              whileHover={{ scale: 1.02 }}
            >
              <div className="text-2xl font-semibold bg-gradient-to-r from-blue-400 to-blue-600 bg-clip-text text-transparent mb-1">$127.43</div>
              <div className="text-xs font-medium text-slate-400 tracking-wider uppercase">Yield Earned</div>
            </motion.div>
            <motion.div 
              className="p-4 bg-slate-900/50 backdrop-blur-xl rounded-xl border border-slate-700/50 hover:border-blue-400/40 transition-all duration-300"
              whileHover={{ scale: 1.02 }}
            >
              <div className="text-2xl font-semibold text-white mb-1">$2,486.50</div>
              <div className="text-xs font-medium text-slate-400 tracking-wider uppercase">Card Spent</div>
            </motion.div>
          </div>
        </div>
      </motion.div>

      {/* Enhanced Tab Navigation */}
      <motion.div 
        className="relative"
      >
        <div className="flex rounded-xl overflow-hidden border border-blue-500/20 bg-gradient-to-br from-slate-800/50 via-blue-900/10 to-slate-900/50 backdrop-blur-xl">
          {/* Sliding indicator */}
          <div 
            className={`absolute top-1 bottom-1 w-1/4 bg-gradient-to-r from-blue-500 via-blue-400 to-blue-600 shadow-lg shadow-blue-500/50 transition-all duration-500 ease-out rounded-lg m-1 ${
              activeTab === 'balance' ? 'left-0' : 
              activeTab === 'yield' ? 'left-1/4' : 
              activeTab === 'transactions' ? 'left-2/4' : 'left-3/4'
            }`}
          ></div>
          
          {['balance', 'yield', 'transactions', 'card'].map((tab) => (
            <button
              key={tab}
              onClick={() => setActiveTab(tab)}
              className={`relative px-6 py-3 text-sm font-medium transition-all duration-500 z-10 flex-1 ${
                activeTab === tab ? 'text-white' : 'text-slate-300 hover:text-white hover:bg-blue-500/10'
              }`}
            >
              <div className={`transition-all duration-300 ${
                activeTab === tab ? 'scale-105' : 'hover:scale-105'
              }`}>
                {tab === 'balance' && 'Wallet Balance'}
                {tab === 'yield' && 'Streaming Yield'}
                {tab === 'transactions' && 'Transaction History'}
                {tab === 'card' && 'Debit Card'}
              </div>
            </button>
          ))}
        </div>
      </motion.div>

      {/* Enhanced Wallet Balance Tab */}
      {activeTab === 'balance' && (
        <motion.div 
          className="rounded-xl p-6 bg-gradient-to-br from-slate-800/50 via-blue-900/10 to-slate-900/50 backdrop-blur-xl border border-blue-500/20 hover:border-blue-400/40 transition-all duration-500"
          whileHover={{ 
            scale: 1.01,
            boxShadow: "0 0 30px rgba(59, 130, 246, 0.2)"
          }}
        >
          {/* Animated background pattern */}
          <div className="absolute inset-0 bg-gradient-to-r from-blue-600/5 via-transparent to-blue-600/5 animate-pulse"></div>

          <div className="relative z-10 space-y-6">
            {/* Portfolio Overview */}
            <div className="text-center">
              <div className="text-xs font-medium text-slate-400 mb-2 tracking-wider uppercase">Total Portfolio Value</div>
              <div className="text-2xl font-semibold bg-gradient-to-r from-blue-400 to-blue-600 bg-clip-text text-transparent mb-6">$8,247.89</div>
              
              <div className="flex justify-center gap-2">
                <motion.button 
                  onClick={() => setShowDepositModal(true)}
                  className="px-4 py-2 font-medium text-xs bg-gradient-to-r from-blue-500 via-blue-400 to-blue-600 text-white rounded-lg shadow-lg shadow-blue-500/30 hover:shadow-blue-500/50 transition-all duration-300"
                  whileHover={{ scale: 1.05 }}
                >
                  Deposit
                </motion.button>
                <motion.button 
                  onClick={() => setShowWithdrawModal(true)}
                  className="px-4 py-2 font-medium text-xs bg-transparent border border-blue-500/20 text-slate-300 rounded-lg hover:border-blue-400/40 hover:text-white transition-all duration-300"
                  whileHover={{ scale: 1.05 }}
                >
                  Withdraw
                </motion.button>
              </div>
            </div>

            {/* Asset Metrics Grid */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
              <motion.div 
                className="p-4 bg-slate-800/50 rounded-xl border border-blue-500/20 text-center hover:border-blue-400/40 transition-all duration-500"
                whileHover={{ 
                  scale: 1.02,
                  boxShadow: "0 0 30px rgba(59, 130, 246, 0.2)"
                }}
              >
                <div className="text-xs font-medium text-slate-400 mb-2 tracking-wider uppercase">Total Assets</div>
                <div className="text-xl font-semibold text-white">3</div>
              </motion.div>
              <motion.div 
                className="p-4 bg-slate-800/50 rounded-xl border border-blue-500/20 text-center hover:border-blue-400/40 transition-all duration-500"
                whileHover={{ 
                  scale: 1.02,
                  boxShadow: "0 0 30px rgba(59, 130, 246, 0.2)"
                }}
              >
                <div className="text-xs font-medium text-slate-400 mb-2 tracking-wider uppercase">24h Change</div>
                <div className="text-xl font-semibold text-green-400">+1.8%</div>
              </motion.div>
              <motion.div 
                className="p-4 bg-slate-800/50 rounded-xl border border-blue-500/20 text-center hover:border-blue-400/40 transition-all duration-500"
                whileHover={{ 
                  scale: 1.02,
                  boxShadow: "0 0 30px rgba(59, 130, 246, 0.2)"
                }}
              >
                <div className="text-xs font-medium text-slate-400 mb-2 tracking-wider uppercase">Yield Today</div>
                <div className="text-xl font-semibold text-green-400">$12.47</div>
              </motion.div>
            </div>

            {/* Asset List */}
            <div>
              <h3 className="text-lg font-semibold mb-4">
                <span className="text-white">Your </span>
                <span className="bg-gradient-to-r from-blue-400 to-blue-600 bg-clip-text text-transparent">Assets</span>
              </h3>
              
              <div className="space-y-3">
                {[
                  { symbol: 'XRP', name: 'XRP', balance: '4,125.67', value: '$2,063.84', change: '+2.4%', logo: '/images/XRP.png' },
                  { symbol: 'RLUSD', name: 'Ripple USD', balance: '3,247.89', value: '$3,247.89', change: '+0.1%', logo: '/images/RLUSD.png' },
                  { symbol: 'ETH', name: 'Ethereum', balance: '0.8943', value: '$2,936.16', change: '+1.8%', logo: '/images/ethereum.svg' }
                ].map((asset, i) => (
                  <motion.div 
                    key={i} 
                    className="flex items-center justify-between p-4 bg-slate-800/50 rounded-xl border border-blue-500/20 hover:border-blue-400/40 transition-all duration-500"
                    whileHover={{ 
                      scale: 1.02,
                      boxShadow: "0 0 30px rgba(59, 130, 246, 0.2)"
                    }}
                  >
                    <div className="flex items-center gap-3">
                      <div className="w-6 h-6 rounded-full overflow-hidden border border-blue-500/20">
                        <img src={asset.logo} alt={asset.symbol} className="w-full h-full object-cover" />
                      </div>
                      <div>
                        <div className="font-medium text-sm text-white">{asset.name}</div>
                        <div className="text-xs font-medium text-slate-400">{asset.balance} {asset.symbol}</div>
                      </div>
                    </div>
                    
                    <div className="text-right">
                      <div className="font-semibold text-sm text-white">{asset.value}</div>
                      <div className={`text-xs font-medium ${asset.change.startsWith('+') ? 'text-green-400' : 'text-red-400'}`}>
                        {asset.change}
                      </div>
                    </div>
                  </motion.div>
                ))}
              </div>
            </div>
          </div>
        </motion.div>
      )}

      {/* Streaming Yield Tab */}
      {activeTab === 'yield' && (
        <motion.div 
          className="rounded-xl p-6 bg-gradient-to-br from-slate-800/50 via-blue-900/10 to-slate-900/50 backdrop-blur-xl border border-blue-500/20 hover:border-blue-400/40 transition-all duration-500"
          whileHover={{ 
            scale: 1.01,
            boxShadow: "0 0 30px rgba(59, 130, 246, 0.2)"
          }}
        >
          {/* Animated background pattern */}
          <div className="absolute inset-0 bg-gradient-to-r from-blue-600/5 via-transparent to-blue-600/5 animate-pulse"></div>

          <div className="relative z-10 space-y-6">
            {/* Header */}
            <div className="text-center">
              <h2 className="text-2xl font-semibold text-white mb-2">
                <span className="text-white">Streaming </span>
                <span className="bg-gradient-to-r from-blue-400 to-blue-600 bg-clip-text text-transparent">Yield</span>
              </h2>
              <p className="text-sm text-slate-300">Real-time yield generation from your deposits</p>
            </div>

            {/* Stats Grid */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
              <motion.div 
                className="p-4 bg-slate-800/50 rounded-xl border border-blue-500/20 text-center hover:border-blue-400/40 transition-all duration-500"
                whileHover={{ 
                  scale: 1.02,
                  boxShadow: "0 0 30px rgba(59, 130, 246, 0.2)"
                }}
              >
                <div className="text-xs font-medium text-slate-400 mb-2 tracking-wider uppercase">Current APY</div>
                <div className="text-2xl font-semibold bg-gradient-to-r from-blue-400 to-blue-600 bg-clip-text text-transparent">4.87%</div>
              </motion.div>
              <motion.div 
                className="p-4 bg-slate-800/50 rounded-xl border border-blue-500/20 text-center hover:border-blue-400/40 transition-all duration-500"
                whileHover={{ 
                  scale: 1.02,
                  boxShadow: "0 0 30px rgba(59, 130, 246, 0.2)"
                }}
              >
                <div className="text-xs font-medium text-slate-400 mb-2 tracking-wider uppercase">Total Earned</div>
                <div className="text-2xl font-semibold text-white">$127.43</div>
              </motion.div>
              <motion.div 
                className="p-4 bg-slate-800/50 rounded-xl border border-blue-500/20 text-center hover:border-blue-400/40 transition-all duration-500"
                whileHover={{ 
                  scale: 1.02,
                  boxShadow: "0 0 30px rgba(59, 130, 246, 0.2)"
                }}
              >
                <div className="text-xs font-medium text-slate-400 mb-2 tracking-wider uppercase">Today's Yield</div>
                <div className="text-2xl font-semibold text-green-400">$0.47</div>
              </motion.div>
            </div>

            {/* Yield Progress */}
            <div className="p-4 bg-slate-800/50 rounded-xl border border-blue-500/20">
              <div className="flex justify-between text-xs mb-2">
                <span className="text-slate-400 font-medium">Daily Yield Progress</span>
                <span className="font-medium text-white">72.4%</span>
              </div>
              <div className="h-1.5 w-full bg-slate-700/50 rounded-full overflow-hidden">
                <motion.div 
                  className="bg-gradient-to-r from-blue-500 via-blue-400 to-blue-600 h-1.5 rounded-full shadow-lg shadow-blue-500/50"
                  initial={{ width: 0 }}
                  animate={{ width: "72.4%" }}
                  transition={{ duration: 1.5, delay: 0.5 }}
                />
              </div>
            </div>

            {/* Yield Sources */}
            <div>
              <h3 className="text-lg font-semibold mb-4 text-white">Yield Sources</h3>
              <div className="space-y-3">
                {[
                  { source: 'Vault Deposits', apy: '5.2%', amount: '$3,247.00', yield: '$43.21' },
                  { source: 'Liquidity Mining', apy: '4.8%', amount: '$2,186.50', yield: '$28.97' },
                  { source: 'Staking Rewards', apy: '4.1%', amount: '$1,856.20', yield: '$19.84' }
                ].map((item, i) => (
                  <motion.div 
                    key={i} 
                    className="flex items-center justify-between p-4 bg-slate-800/50 rounded-xl border border-blue-500/20 hover:border-blue-400/40 transition-all duration-500"
                    whileHover={{ 
                      scale: 1.02,
                      boxShadow: "0 0 30px rgba(59, 130, 246, 0.2)"
                    }}
                  >
                    <div>
                      <div className="font-medium text-sm text-white">{item.source}</div>
                      <div className="text-xs font-medium text-slate-400">{item.amount} deposited</div>
                    </div>
                    <div className="text-right">
                      <div className="font-semibold text-sm bg-gradient-to-r from-blue-400 to-blue-600 bg-clip-text text-transparent">{item.apy}</div>
                      <div className="text-xs font-medium text-green-400">{item.yield} earned</div>
                    </div>
                  </motion.div>
                ))}
              </div>
            </div>
          </div>
        </motion.div>
      )}

      {/* Transaction History Tab */}
      {activeTab === 'transactions' && (
        <motion.div 
          className="rounded-xl p-6 bg-gradient-to-br from-slate-800/50 via-blue-900/10 to-slate-900/50 backdrop-blur-xl border border-blue-500/20 hover:border-blue-400/40 transition-all duration-500"
          whileHover={{ 
            scale: 1.01,
            boxShadow: "0 0 30px rgba(59, 130, 246, 0.2)"
          }}
        >
          {/* Animated background pattern */}
          <div className="absolute inset-0 bg-gradient-to-r from-blue-600/5 via-transparent to-blue-600/5 animate-pulse"></div>

          <div className="relative z-10">
            <h2 className="text-lg font-semibold mb-6">
              <span className="text-white">Transaction </span>
              <span className="bg-gradient-to-r from-blue-400 to-blue-600 bg-clip-text text-transparent">History</span>
            </h2>
            
            <div className="space-y-3">
              {[
                { type: 'Deposit', amount: '+1,000', asset: 'RLUSD', time: '2 hours ago', status: 'Completed' },
                { type: 'Swap', amount: '-500', asset: 'XRP', time: '4 hours ago', status: 'Completed' },
                { type: 'Yield', amount: '+12.47', asset: 'RLUSD', time: '6 hours ago', status: 'Completed' },
                { type: 'Bridge', amount: '-250', asset: 'RLUSD', time: '2 days ago', status: 'Completed' }
              ].map((tx, i) => (
                <motion.div 
                  key={i} 
                  className="flex items-center justify-between p-4 bg-slate-800/50 rounded-xl border border-blue-500/20 hover:border-blue-400/40 transition-all duration-500"
                  whileHover={{ 
                    scale: 1.02,
                    boxShadow: "0 0 30px rgba(59, 130, 246, 0.2)"
                  }}
                >
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-blue-500/20 to-blue-600/20 flex items-center justify-center">
                      {tx.type === 'Deposit' && (
                        <svg className="w-4 h-4 text-blue-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 14l-7 7m0 0l-7-7m7 7V3" />
                        </svg>
                      )}
                      {tx.type === 'Swap' && (
                        <svg className="w-4 h-4 text-blue-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7h12m0 0l-4-4m4 4l-4 4m0 6H4m0 0l4 4m-4-4l4-4" />
                        </svg>
                      )}
                      {tx.type === 'Yield' && (
                        <svg className="w-4 h-4 text-blue-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1" />
                        </svg>
                      )}
                      {tx.type === 'Bridge' && (
                        <svg className="w-4 h-4 text-blue-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" />
                        </svg>
                      )}
                    </div>
                    <div>
                      <div className="font-medium text-sm text-white">{tx.type}</div>
                      <div className="text-xs font-medium text-slate-400">{tx.time}</div>
                    </div>
                  </div>
                  
                  <div className="text-right">
                    <div className={`font-semibold text-sm ${
                      tx.amount.startsWith('+') ? 'text-green-400' : 'text-white'
                    }`}>
                      {tx.amount} {tx.asset}
                    </div>
                    <div className="text-xs font-medium text-slate-300">
                      {tx.status}
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </motion.div>
      )}

      {/* Debit Card Tab */}
      {activeTab === 'card' && (
        <motion.div 
          className="rounded-xl p-6 bg-gradient-to-br from-slate-800/50 via-blue-900/10 to-slate-900/50 backdrop-blur-xl border border-blue-500/20 hover:border-blue-400/40 transition-all duration-500"
          whileHover={{ 
            scale: 1.01,
            boxShadow: "0 0 30px rgba(59, 130, 246, 0.2)"
          }}
        >
          {/* Animated background pattern */}
          <div className="absolute inset-0 bg-gradient-to-r from-blue-600/5 via-transparent to-blue-600/5 animate-pulse"></div>

          <div className="relative z-10 space-y-6">
            {/* Header */}
            <div className="text-center">
              <h2 className="text-2xl font-semibold text-white mb-2">
                <span className="text-white">RippleOne </span>
                <span className="bg-gradient-to-r from-blue-400 to-blue-600 bg-clip-text text-transparent">Debit Card</span>
              </h2>
              <p className="text-sm text-slate-300">Spend your crypto anywhere in the world</p>
            </div>

            {/* Debit Card Graphic */}
            <div className="flex justify-center mb-6">
              <motion.div 
                className="relative w-full max-w-sm aspect-[1.586/1] rounded-xl bg-gradient-to-br from-blue-600 via-blue-500 to-blue-700 p-4 shadow-xl shadow-blue-500/30"
                whileHover={{ 
                  scale: 1.02,
                  rotateY: 5,
                  rotateX: 5,
                  boxShadow: "0 20px 40px rgba(59, 130, 246, 0.3)"
                }}
                style={{ transformStyle: "preserve-3d" }}
              >
                {/* Card Background Effects */}
                <div className="absolute inset-0 rounded-xl overflow-hidden">
                  <div className="absolute inset-0 bg-gradient-to-br from-white/20 to-transparent"></div>
                  <div className="absolute top-0 right-0 w-20 h-20 bg-white/10 rounded-full -translate-y-10 translate-x-10"></div>
                  <div className="absolute bottom-0 left-0 w-16 h-16 bg-white/5 rounded-full translate-y-8 -translate-x-8"></div>
                </div>

                {/* Card Content */}
                <div className="relative z-10 h-full flex flex-col justify-between text-white">
                  {/* Card Header */}
                  <div className="flex justify-between items-start">
                    <div className="flex items-center gap-1.5">
                      <div className="w-6 h-6 rounded-full bg-white/20 flex items-center justify-center">
                        <img src="/images/rippleone-logo.svg" alt="RippleOne" className="w-4 h-4" />
                      </div>
                      <span className="font-bold text-sm tracking-wide">RippleOne</span>
                    </div>
                    {/* Contactless Payment Symbol */}
                    <div className="flex gap-0.5">
                      <div className="w-3 h-3 border border-white/60 rounded-full"></div>
                      <div className="w-3 h-3 border border-white/60 rounded-full"></div>
                      <div className="w-3 h-3 border border-white/60 rounded-full"></div>
                    </div>
                  </div>

                  {/* Card Number */}
                  <div className="font-mono text-lg tracking-wider">
                    5234 •••• •••• 8901
                  </div>

                  {/* Card Footer */}
                  <div className="flex justify-between items-end">
                    <div>
                      <div className="text-xs opacity-80 mb-1">CARD HOLDER</div>
                      <div className="font-semibold text-sm">JOHN DOE</div>
                    </div>
                    <div>
                      <div className="text-xs opacity-80 mb-1">EXPIRES</div>
                      <div className="font-semibold text-sm">12/28</div>
                    </div>
                    <div className="text-right">
                      <div className="text-sm font-bold">VISA</div>
                    </div>
                  </div>
                </div>
              </motion.div>
            </div>

            {/* Card Stats Grid */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-3">
              <motion.div 
                className="p-3 bg-slate-800/50 rounded-lg border border-blue-500/20 text-center hover:border-blue-400/40 transition-all duration-500"
                whileHover={{ 
                  scale: 1.02,
                  boxShadow: "0 0 20px rgba(59, 130, 246, 0.2)"
                }}
              >
                <div className="text-xs font-medium text-slate-400 mb-1 tracking-wider uppercase">Total Spent</div>
                <div className="text-lg font-semibold text-white">$2,486.50</div>
              </motion.div>
              <motion.div 
                className="p-3 bg-slate-800/50 rounded-lg border border-blue-500/20 text-center hover:border-blue-400/40 transition-all duration-500"
                whileHover={{ 
                  scale: 1.02,
                  boxShadow: "0 0 20px rgba(59, 130, 246, 0.2)"
                }}
              >
                <div className="text-xs font-medium text-slate-400 mb-1 tracking-wider uppercase">Cashback Rate</div>
                <div className="text-lg font-semibold bg-gradient-to-r from-blue-400 to-blue-600 bg-clip-text text-transparent">1.5%</div>
              </motion.div>
              <motion.div 
                className="p-3 bg-slate-800/50 rounded-lg border border-blue-500/20 text-center hover:border-blue-400/40 transition-all duration-500"
                whileHover={{ 
                  scale: 1.02,
                  boxShadow: "0 0 20px rgba(59, 130, 246, 0.2)"
                }}
              >
                <div className="text-xs font-medium text-slate-400 mb-1 tracking-wider uppercase">Cashback Earned</div>
                <div className="text-lg font-semibold text-green-400">$37.30</div>
              </motion.div>
            </div>

            {/* Action Buttons */}
            <div className="flex gap-3 justify-center">
              <motion.button 
                onClick={() => setShowTopUpModal(true)}
                className="px-6 py-2.5 font-medium text-sm bg-gradient-to-r from-blue-500 via-blue-400 to-blue-600 text-white rounded-lg shadow-lg shadow-blue-500/50 hover:shadow-blue-500/70 transition-all duration-300"
                whileHover={{ scale: 1.05 }}
              >
                Top Up Card
              </motion.button>
              <motion.button 
                onClick={() => setShowManageCardModal(true)}
                className="px-6 py-2.5 font-medium text-sm bg-transparent border border-blue-500/20 text-slate-300 rounded-lg hover:border-blue-400/40 hover:text-white transition-all duration-300"
                whileHover={{ scale: 1.05 }}
              >
                Manage Card
              </motion.button>
            </div>

            {/* KYC Verification */}
            <div className="p-4 bg-slate-800/50 rounded-xl border border-blue-500/20">
              <div className="text-center mb-4">
                <h3 className="text-lg font-semibold text-white mb-2">Identity Verification</h3>
                <p className="text-sm text-slate-300">Complete KYC to unlock full card features</p>
              </div>
              
              <div className="space-y-3 mb-4">
                {[
                  { step: 'Personal Information', status: 'completed' },
                  { step: 'Document Upload', status: 'completed' },
                  { step: 'Identity Verification', status: 'pending' },
                  { step: 'Address Verification', status: 'pending' }
                ].map((item, i) => (
                  <div key={i} className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-medium ${
                        item.status === 'completed' ? 'bg-green-500' : 'bg-slate-600'
                      }`}>
                        {item.status === 'completed' ? (
                          <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                          </svg>
                        ) : (
                          i + 1
                        )}
                      </div>
                      <span className="text-sm font-medium text-white">{item.step}</span>
                    </div>
                    <span className={`text-xs font-medium px-2 py-1 rounded ${
                      item.status === 'completed' ? 'bg-green-500/20 text-green-400' : 'bg-slate-600/20 text-slate-300'
                    }`}>
                      {item.status === 'completed' ? 'Completed' : 'Pending'}
                    </span>
                  </div>
                ))}
              </div>
              
              <div className="mb-2">
                <div className="flex justify-between text-xs mb-2">
                  <span className="text-slate-400 font-medium">Verification Progress</span>
                  <span className="font-medium text-white">50%</span>
                </div>
                <div className="h-1.5 w-full bg-slate-700/50 rounded-full overflow-hidden">
                  <motion.div 
                    className="bg-gradient-to-r from-blue-500 via-blue-400 to-blue-600 h-1.5 rounded-full shadow-lg shadow-blue-500/50"
                    initial={{ width: 0 }}
                    animate={{ width: "50%" }}
                    transition={{ duration: 1.5, delay: 0.5 }}
                  />
                </div>
              </div>
            </div>

            {/* Recent Card Transactions */}
            <div>
              <h3 className="text-lg font-semibold mb-4 text-white">Recent Transactions</h3>
              <div className="space-y-3">
                {[
                  { merchant: 'Amazon', amount: '-$129.99', time: '2 hours ago', category: 'Shopping', type: 'shopping' },
                  { merchant: 'Starbucks', amount: '-$5.47', time: '1 day ago', category: 'Food & Drink', type: 'food' },
                  { merchant: 'Gas Station', amount: '-$67.23', time: '2 days ago', category: 'Transport', type: 'gas' },
                  { merchant: 'Netflix', amount: '-$15.99', time: '1 week ago', category: 'Entertainment', type: 'entertainment' }
                ].map((tx, i) => (
                  <motion.div 
                    key={i} 
                    className="flex items-center justify-between p-4 bg-slate-800/50 rounded-xl border border-blue-500/20 hover:border-blue-400/40 transition-all duration-500"
                    whileHover={{ 
                      scale: 1.02,
                      boxShadow: "0 0 30px rgba(59, 130, 246, 0.2)"
                    }}
                  >
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-blue-500/20 to-blue-600/20 flex items-center justify-center">
                        {tx.type === 'shopping' && (
                          <svg className="w-4 h-4 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 3h2l.4 2M7 13h10l4-8H5.4m0 0L7 13m0 0l-1.1-3M7 13l-1.1 3m0 0H19m-4 3a1 1 0 11-2 0 1 1 0 012 0zm-7 0a1 1 0 11-2 0 1 1 0 012 0z" />
                          </svg>
                        )}
                        {tx.type === 'food' && (
                          <svg className="w-4 h-4 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 14l9-5-9-5-9 5 9 5z" />
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 14v7" />
                          </svg>
                        )}
                        {tx.type === 'gas' && (
                          <svg className="w-4 h-4 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 14v3a2 2 0 002 2h4a2 2 0 002-2v-3M7 10h10M9 4h6v4a2 2 0 01-2 2H11a2 2 0 01-2-2V4z" />
                          </svg>
                        )}
                        {tx.type === 'entertainment' && (
                          <svg className="w-4 h-4 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14.828 14.828a4 4 0 01-5.656 0M9 10h1.01M15 10h1.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                          </svg>
                        )}
                      </div>
                      <div>
                        <div className="font-medium text-sm text-white">{tx.merchant}</div>
                        <div className="text-xs font-medium text-slate-400">{tx.category} • {tx.time}</div>
                      </div>
                    </div>
                    <div className="font-semibold text-sm text-white">{tx.amount}</div>
                  </motion.div>
                ))}
              </div>
            </div>
          </div>
        </motion.div>
      )}

      {/* Show modals if active */}
      {showWithdrawModal && <WithdrawModal />}
      {showDepositModal && <DepositModal />}
      {showTopUpModal && <TopUpCardModal />}
      {showManageCardModal && <ManageCardModal />}
    </motion.div>
  );
};

export default Wallet; 